import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { cva } from "class-variance-authority";
import * as React from "react";

import { faCheck } from "@performyard/fontawesome/pro-regular-svg-icons";

// Utils
import { cn } from "../../../lib/utils";

// Components
import { Icon } from "../Icon";

const checkboxVariants = cva(
	[
		// Base styles
		"shrink-0 cursor-pointer rounded-0.5",
		// Border and background
		"border border-neutral-3 bg-transparent",
		// Focus states
		"focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-1",
		// Checked state
		"data-[state=checked]:border-primary-5 data-[state=checked]:bg-primary-5",
		// Peer class for form validation
		"box-shadow-none peer shadow-none",
		// remove native active styles
		"active:border-solid active:shadow-none active:outline-none active:ring-0",
		// remove native styles
		"border-solid shadow-none outline-none ring-0",
	],
	{
		variants: {
			size: {
				small: "size-4",
				large: "size-6",
			},
			disabled: {
				true: "cursor-not-allowed opacity-disabled",
				false: "hover:border-neutral-5 active:border-neutral-5",
			},
			errored: {
				true: "!border-errored",
				false: "",
			},
		},
		defaultVariants: {
			size: "small",
			disabled: false,
			errored: false,
		},
	}
);

/**
 * Props for the Checkbox component
 */
type CheckboxProps = {
	/** The checked state of the checkbox */
	checked?: boolean;
	/** Whether the checkbox is disabled */
	disabled?: boolean;
	/** Whether the checkbox is in an error state */
	errored?: boolean;
	/** Additional className to apply to the checkbox */
	dangerouslySetClassName?: string;
	/** The size of the checkbox */
	size?: "small" | "large";
	/** The unique identifier for the checkbox */
	id: string;
	/** Callback fired when the checked state changes */
	onCheckedChange?: (checked: boolean) => void;
	/** Test ID for testing purposes */
	"data-testid"?: string;
	/**
	 * Required for accessibility. Either provide aria-label or aria-labelledby
	 * @see https://www.w3.org/WAI/ARIA/apg/patterns/checkbox/
	 */
	"aria-label"?: string;
	/**
	 * Required for accessibility. Either provide aria-label or aria-labelledby
	 * @see https://www.w3.org/WAI/ARIA/apg/patterns/checkbox/
	 */
	"aria-labelledby"?: string;
};

/**
 * A checkbox component that can provide a boolean selection state. This component does not have a label, that is handled by the parent component.
 * For several checkboxes with labels, use the `CheckboxGroup` component.
 */
const Checkbox = React.forwardRef<HTMLButtonElement, CheckboxProps>(
	({ dangerouslySetClassName, size, errored, disabled, ...props }, ref) => {
		return (
			<CheckboxPrimitive.Root
				ref={ref}
				className={cn(checkboxVariants({ size, disabled, errored }), dangerouslySetClassName)}
				disabled={disabled}
				aria-disabled={disabled}
				aria-invalid={errored}
				{...props}
			>
				<CheckboxPrimitive.Indicator className={cn("flex items-center justify-center text-current")}>
					<Icon
						icon={faCheck}
						size={size === "large" ? 24 : 16}
						color="neutral-0"
						dangerouslySetClassName="w-full h-full p-0"
					/>
				</CheckboxPrimitive.Indicator>
			</CheckboxPrimitive.Root>
		);
	}
);
Checkbox.displayName = "checkbox";

export { Checkbox, type CheckboxProps };
